import { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { api } from '../../lib/api'

interface SignerData {
  signer: { token: string; nome: string }
  document: { countryCode: string }
  countryConfig: { selfieRecommended: boolean }
}

interface Props {
  signerData: SignerData
  onComplete: () => void
  onSkip: () => void
}

/* ── Visual guide shown in instruction screen ── */
function IdGuide({ faceLabel, docLabel }: { faceLabel: string; docLabel: string }) {
  return (
    <svg viewBox="0 0 260 180" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[260px] mx-auto">
      {/* Camera frame */}
      <rect x="1" y="1" width="258" height="178" rx="4" stroke="#E8E4DC" strokeWidth="1.5"/>

      {/* Face oval — lado esquerdo, sem encostar no cartão */}
      <ellipse cx="80" cy="72" rx="40" ry="48" stroke="#FF5B2E" strokeWidth="2" strokeDasharray="6 3"/>
      <circle cx="68" cy="66" r="4" fill="#FF5B2E" fillOpacity="0.4"/>
      <circle cx="92" cy="66" r="4" fill="#FF5B2E" fillOpacity="0.4"/>
      <path d="M68 86 Q80 98 92 86" stroke="#FF5B2E" strokeWidth="2" strokeLinecap="round" fill="none"/>

      {/* ID card — lado direito */}
      <rect x="150" y="72" width="92" height="58" rx="3" fill="#FFF8F0" stroke="#FF5B2E" strokeWidth="2"/>
      <rect x="158" y="80" width="30" height="34" rx="2" fill="#E8E4DC"/>
      <rect x="196" y="80" width="38" height="6" rx="1" fill="#E8E4DC"/>
      <rect x="196" y="92" width="26" height="5" rx="1" fill="#E8E4DC"/>
      <rect x="158" y="120" width="76" height="4" rx="1" fill="#E8E4DC" fillOpacity="0.5"/>

      {/* Mão segurando o cartão */}
      <path d="M166 148 Q160 142 160 130" stroke="#3A3A38" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M176 150 Q171 143 171 130" stroke="#3A3A38" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M186 149 Q181 142 181 130" stroke="#3A3A38" strokeWidth="2.5" strokeLinecap="round"/>

      {/* Seta do rosto para o documento */}
      <path d="M124 92 Q138 98 146 100" stroke="#FF5B2E" strokeWidth="1.5" strokeDasharray="3 2" markerEnd="url(#arr)"/>
      <defs>
        <marker id="arr" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0 0 L6 3 L0 6 Z" fill="#FF5B2E"/>
        </marker>
      </defs>

      {/* Rótulos — abaixo de cada elemento, sem sobrepor o desenho */}
      <text x="80" y="140" textAnchor="middle" fontSize="9" fill="#8A8A85" fontFamily="monospace" letterSpacing="1">{faceLabel}</text>
      <text x="196" y="168" textAnchor="middle" fontSize="9" fill="#8A8A85" fontFamily="monospace" letterSpacing="1">{docLabel}</text>
    </svg>
  )
}

/* ── Camera overlay guide ── */
function CameraOverlay({ faceLabel, docLabel }: { faceLabel: string; docLabel: string }) {
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 640 480" preserveAspectRatio="xMidYMid slice">
      {/* Face zone */}
      <ellipse cx="260" cy="210" rx="140" ry="165" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeDasharray="8 4" fill="none"/>
      <text x="260" y="400" textAnchor="middle" fontSize="14" fill="rgba(255,255,255,0.7)" fontFamily="monospace" letterSpacing="2">{faceLabel}</text>

      {/* Document zone */}
      <rect x="430" y="290" width="180" height="120" rx="6" stroke="rgba(255,91,46,0.8)" strokeWidth="2" strokeDasharray="8 4" fill="rgba(255,91,46,0.05)"/>
      <text x="520" y="435" textAnchor="middle" fontSize="14" fill="rgba(255,91,46,0.9)" fontFamily="monospace" letterSpacing="2">{docLabel}</text>
    </svg>
  )
}

type Stage = 'instruction' | 'camera' | 'preview' | 'mobile'

export default function Step2Selfie({ signerData, onComplete, onSkip }: Props) {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()

  const [stage, setStage] = useState<Stage>('instruction')
  const [capturedImage, setCapturedImage] = useState<string | null>(null)
  const [cameraActive, setCameraActive] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [consent, setConsent] = useState(false)
  const [cameraAvailable, setCameraAvailable] = useState(true)
  const [mobileQrUrl, setMobileQrUrl] = useState<string | null>(null)
  const [generatingQr, setGeneratingQr] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const selfieRequired = signerData.countryConfig.selfieRecommended

  const startCamera = useCallback(async () => {
    setError('')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      })
      streamRef.current = stream
      setStage('camera')
      setCameraActive(true)
    } catch {
      setCameraAvailable(false)
      setStage('mobile')
      setError(t('sign.selfie.noCameraError', 'Unable to access camera. Use your phone instead.'))
    }
  }, [t])

  useEffect(() => {
    if (cameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current
      videoRef.current.play().catch(() => {})
    }
  }, [cameraActive])

  function stopCamera() {
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
    setCameraActive(false)
  }

  function capturePhoto() {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) return
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0)
    setCapturedImage(canvas.toDataURL('image/jpeg', 0.85))
    stopCamera()
    setCameraActive(false)
    setStage('preview')
  }

  function retake() {
    setCapturedImage(null)
    setError('')
    startCamera()
  }

  async function handleUpload() {
    if (!capturedImage) return
    setError('')
    setUploading(true)
    try {
      const blob = await (await fetch(capturedImage)).blob()
      const formData = new FormData()
      formData.append('selfie', blob, 'selfie.jpg')
      await api.upload(`/api/sign/${token}/selfie`, formData)
      onComplete()
    } catch (err: unknown) {
      const body = (err as { body?: { error?: string } })?.body
      if (body?.error === 'document_not_detected') {
        setError(t('sign.selfie.noDocumentError', 'We could not detect an identity document in your photo. Please retake the selfie while clearly holding your document next to your face.'))
      } else if (body?.error === 'face_not_detected') {
        setError(t('sign.selfie.noFaceError', 'We could not detect a face in your photo. Please make sure your face is clearly visible.'))
      } else {
        setError(t('sign.selfie.uploadError', 'Failed to upload selfie. Please try again.'))
      }
      setStage('instruction')
    } finally {
      setUploading(false)
    }
  }

  async function handleSkip() {
    try { await api.post(`/api/sign/${token}/selfie`, { skipped: true }) } catch { /* ignore */ }
    onSkip()
  }

  async function generateMobileQr() {
    setGeneratingQr(true)
    try {
      const res = await api.post<{ mobileUrl: string }>(`/api/sign/${token}/selfie/mobile-qr`)
      setMobileQrUrl(res.mobileUrl)
      pollRef.current = setInterval(async () => {
        try {
          const data = await api.get<{ signer: { status: string } }>(`/api/sign/${token}`)
          if (data.signer.status !== 'PENDING_SELFIE') {
            if (pollRef.current) clearInterval(pollRef.current)
            onComplete()
          }
        } catch { /* ignore polling errors */ }
      }, 3000)
    } catch {
      setError(t('sign.selfie.qrError', 'Failed to generate QR code.'))
    } finally {
      setGeneratingQr(false)
    }
  }

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current) }, [])

  return (
    <div className="space-y-6">
      <div className="bg-white border border-pakt-line p-6">

        <h2 className="text-xl font-semibold text-pakt-ink mb-1">
          {t('sign.selfie.title', 'Identity Verification')}
        </h2>
        <p className="text-sm text-gray-500 mb-5">
          {t('sign.selfie.subtitle', 'Take a selfie holding your identity document — passport, national ID, or driver\'s license.')}
        </p>

        {/* Error banner */}
        {error && (
          <div className="mb-5 p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* ── Instruction stage ── */}
        {stage === 'instruction' && (
          <div className="space-y-6">
            <div className="bg-pakt-surface border border-pakt-line p-5">
              <div className="font-pakt-mono text-[10px] tracking-[0.2em] text-pakt-orange uppercase mb-3">
                {t('sign.selfie.howToTitle', 'How to take the photo')}
              </div>
              <IdGuide faceLabel={t('sign.selfie.guideFace', 'YOUR FACE')} docLabel={t('sign.selfie.guideDocument', 'YOUR DOCUMENT')} />
              <ol className="mt-4 space-y-2 text-sm text-gray-700">
                <li className="flex gap-3">
                  <span className="font-pakt-mono text-pakt-orange text-xs mt-0.5">01</span>
                  {t('sign.selfie.step1', 'Hold your identity document (passport, national ID, or driver\'s license) beside your face')}
                </li>
                <li className="flex gap-3">
                  <span className="font-pakt-mono text-pakt-orange text-xs mt-0.5">02</span>
                  {t('sign.selfie.step2', 'Make sure both your face and the document are clearly visible')}
                </li>
                <li className="flex gap-3">
                  <span className="font-pakt-mono text-pakt-orange text-xs mt-0.5">03</span>
                  {t('sign.selfie.step3', 'Use good lighting — avoid strong backlighting')}
                </li>
              </ol>
            </div>

            {/* Consent + disclosure de sub-processador (LGPD Art. 9/11/33) */}
            <div className="border border-pakt-line p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                  className="mt-0.5 border-gray-300 text-pakt-orange focus:ring-pakt-orange"
                />
                <span className="text-sm text-gray-700">
                  {t('sign.selfie.consent', 'I consent to my photo being captured for identity verification. This image will be stored securely and used only to validate the signing process.')}
                </span>
              </label>
              <div className="mt-3 ml-7 text-xs text-gray-500 leading-relaxed">
                <p>
                  {t(
                    'sign.selfie.disclosure',
                    'Sua foto é analisada por inteligência artificial (Anthropic, EUA) para verificar a presença de rosto + documento, e armazenada criptografada nos nossos servidores (Hetzner, Alemanha). Apagada após a finalização do contrato conforme política de retenção do plano.'
                  )}
                  {' '}
                  <a
                    href="/sub-processadores"
                    target="_blank"
                    rel="noopener"
                    className="text-pakt-orange hover:underline"
                  >
                    {t('sign.selfie.disclosureLink', 'Ver lista completa de sub-processadores')}
                  </a>
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={startCamera}
                disabled={!consent}
                className="flex-1 py-3 px-6 bg-pakt-orange text-white font-semibold hover:bg-pakt-orange-dk disabled:opacity-40 disabled:cursor-not-allowed transition text-sm"
              >
                {t('sign.selfie.openCamera', 'Open Camera')}
              </button>
              <button
                onClick={() => { setStage('mobile') }}
                disabled={!consent}
                className="py-3 px-6 border border-pakt-line text-pakt-ink-2 font-medium hover:bg-pakt-surface disabled:opacity-40 transition text-sm"
              >
                {t('sign.selfie.usePhone', 'Use phone')}
              </button>
            </div>
          </div>
        )}

        {/* ── Camera stage ── */}
        {stage === 'camera' && (
          <div className="flex flex-col items-center">
            <div className="relative w-full max-w-md bg-black overflow-hidden">
              <video ref={videoRef} autoPlay playsInline muted className="w-full block scale-x-[-1]" />
              <CameraOverlay faceLabel={t('sign.selfie.zoneFace', 'FACE')} docLabel={t('sign.selfie.zoneDocument', 'DOCUMENT')} />
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center font-pakt-mono tracking-wide uppercase">
              {t('sign.selfie.cameraGuide', 'Position face + document within the guides')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-4 w-full max-w-md">
              <button
                onClick={capturePhoto}
                className="flex-1 py-3 bg-pakt-orange text-white font-semibold hover:bg-pakt-orange-dk transition"
              >
                {t('sign.selfie.capture', 'Take Photo')}
              </button>
              <button
                onClick={() => { stopCamera(); setStage('instruction') }}
                className="py-3 px-6 border border-pakt-line text-pakt-ink-2 hover:bg-pakt-surface transition"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        )}

        {/* ── Preview stage ── */}
        {stage === 'preview' && capturedImage && (
          <div className="flex flex-col items-center">
            <div className="w-full max-w-md border border-pakt-line overflow-hidden">
              <img src={capturedImage} alt={t('sign.selfie.previewAlt', 'Selfie preview')} className="w-full block scale-x-[-1]" />
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center font-pakt-mono tracking-wide uppercase">
              {t('sign.selfie.checkBothVisible', 'Check that both your face and document are visible')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-4 w-full max-w-md">
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="flex-1 py-3 bg-pakt-orange text-white font-semibold hover:bg-pakt-orange-dk disabled:opacity-50 transition"
              >
                {uploading
                  ? t('sign.selfie.uploading', 'Verifying...')
                  : t('sign.selfie.upload', 'Use This Photo')}
              </button>
              <button
                onClick={retake}
                disabled={uploading}
                className="py-3 px-6 border border-pakt-line text-pakt-ink-2 hover:bg-pakt-surface disabled:opacity-50 transition"
              >
                {t('sign.selfie.retake', 'Retake')}
              </button>
            </div>
          </div>
        )}

        {/* ── Mobile QR stage ── */}
        {stage === 'mobile' && (
          <div className="text-center space-y-4 py-4">
            {!cameraAvailable && (
              <p className="text-sm text-gray-600">{t('sign.selfie.noCamera', 'No camera detected on this device.')}</p>
            )}
            <p className="text-sm text-gray-600">
              {t('sign.selfie.mobileInstructions', 'Scan the QR code with your phone to take the selfie. Remember to hold your identity document next to your face.')}
            </p>
            {mobileQrUrl ? (
              <div className="flex flex-col items-center gap-3">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(mobileQrUrl)}`}
                  alt="QR Code"
                  className="w-48 h-48"
                />
                <p className="text-xs text-gray-400 break-all max-w-xs">{mobileQrUrl}</p>
                <div className="flex items-center gap-2 text-sm text-gray-500 font-pakt-mono text-xs tracking-widest uppercase">
                  <span className="w-2 h-2 rounded-full bg-pakt-orange animate-pulse" />
                  {t('sign.selfie.waitingMobile', 'Waiting for selfie...')}
                </div>
              </div>
            ) : (
              <button
                onClick={generateMobileQr}
                disabled={generatingQr || !consent}
                className="px-6 py-3 bg-pakt-orange text-white font-semibold hover:bg-pakt-orange-dk disabled:opacity-50 transition"
              >
                {generatingQr
                  ? t('sign.selfie.generating', 'Generating...')
                  : t('sign.selfie.generateQr', 'Generate QR Code')}
              </button>
            )}
            {cameraAvailable && !mobileQrUrl && (
              <button
                onClick={() => setStage('instruction')}
                className="text-sm text-pakt-orange hover:underline block mx-auto"
              >
                {t('sign.selfie.useWebcam', 'Use webcam instead')}
              </button>
            )}
          </div>
        )}

        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Skip option */}
      {!selfieRequired && (
        <div className="border border-amber-200 bg-amber-50 p-4 text-center">
          <p className="text-sm text-amber-800 mb-3">
            {t('sign.selfie.skipInfo', 'Selfie verification is optional for your country. You can skip this step.')}
          </p>
          <button
            onClick={handleSkip}
            className="px-4 py-2 text-sm font-medium text-amber-700 border border-amber-300 hover:bg-amber-100 transition"
          >
            {t('sign.selfie.skip', 'Skip Selfie Verification')}
          </button>
        </div>
      )}
    </div>
  )
}
